import { createORPCClient, ORPCError } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { cheryContract, type TokensOutput } from 'chery-contracts';
import { resolveApiBaseUrl } from '../env-config';
import { authTokenStore } from './auth-token';
import { createSingleFlight, shouldRefreshAfter } from './refresh-policy';

export const LOGIN_PATH = '/login';

export type CheryClient = ContractRouterClient<typeof cheryContract>;

/** Raised to every waiting caller when the refresh token itself is refused. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

const sessionListeners = new Set<(session: TokensOutput | null) => void>();

/** `AuthContext` listens here so a silent refresh (or its failure) updates the signed-in user. */
export const onSessionChange = (listener: (session: TokensOutput | null) => void): (() => void) => {
  sessionListeners.add(listener);

  return () => sessionListeners.delete(listener);
};

/** Drops the stored session and bounces the browser to `/login` — unless we are already there. */
const endSession = (): void => {
  authTokenStore.clear();
  sessionListeners.forEach((listener) => listener(null));

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
};

/** A bare client with no interceptors: the refresh call must never trigger a refresh of its own. */
const refreshClient: CheryClient = createORPCClient(new OpenAPILink(cheryContract, { url: resolveApiBaseUrl() }));

/**
 * Exchanges the stored refresh token for a new pair (the server rotates it: the old one is now dead). Shared by
 * every concurrent 401, so a burst of expired queries spends exactly one single-use refresh token.
 */
const refreshSession = createSingleFlight(async (): Promise<TokensOutput> => {
  const refreshToken = authTokenStore.getRefreshToken();

  if (!refreshToken) {
    throw new SessionExpiredError();
  }

  try {
    const session = await refreshClient.auth.refresh({ refreshToken });

    authTokenStore.setSession(session);
    sessionListeners.forEach((listener) => listener(session));

    return session;
  } catch {
    endSession();

    throw new SessionExpiredError();
  }
});

const link = new OpenAPILink(cheryContract, {
  url: resolveApiBaseUrl(),
  headers: () => {
    const accessToken = authTokenStore.getAccessToken();

    return accessToken ? { authorization: `Bearer ${accessToken}` } : {};
  },
  interceptors: [
    // Refresh-on-401: an expired access token is renewed once and the call retried with the new one (the
    // `headers` callback above runs again inside `next()`). A refused refresh ends the session.
    async ({ next, path }) => {
      try {
        return await next();
      } catch (error) {
        const status = error instanceof ORPCError ? error.status : undefined;

        if (!shouldRefreshAfter(path, status, authTokenStore.getRefreshToken() !== null)) {
          throw error;
        }

        await refreshSession();

        try {
          return await next();
        } catch (retryError) {
          // A fresh access token that is still refused means the session is gone for good.
          if (retryError instanceof ORPCError && retryError.status === 401) {
            endSession();
          }

          throw retryError;
        }
      }
    },
  ],
});

export const orpcClient: CheryClient = createORPCClient(link);

export const isUnauthorizedError = (error: unknown): boolean => error instanceof ORPCError && error.status === 401;
